import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';

const siteUrl = 'https://www.91short.com';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.cover,
        url: `${siteUrl}/article/${article.slug}`,
        publisher: { '@type': 'Organization', name: '91short短视频平台', url: siteUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: article.category, item: `${siteUrl}/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 91short短视频`}
        description={article.excerpt}
        keywords={`91short,${article.tags.join(',')},短视频文章`}
        canonicalUrl={`${siteUrl}/article/${article.slug}`}
        ogType="article"
        schemaData={articleSchema}
      />

      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{article.category}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 line-clamp-1">{article.title}</span>
          </nav>

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="font-medium text-foreground-950">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span>{article.date}</span>
              <span className="text-foreground-300">|</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden mb-10 aspect-[2/1] bg-background-200">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
              prose-strong:text-foreground-950
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-img:rounded-2xl
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-5
              [&_ul]:mb-5 [&_ul]:pl-5
              [&_li]:mb-2
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                >
                  <div className="rounded-xl overflow-hidden mb-4 aspect-[16/9] bg-background-200">
                    <img
                      src={rel.cover}
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span className="text-xs text-primary-600 font-medium">{rel.category}</span>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mt-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {rel.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-2">{rel.date} · {rel.readTime}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}